import { ContentDocument, ContentSummary, Novel } from './types';
import { toTimestamp } from './date-utils';

/**
 * Calculates word count from content, stripping HTML tags.
 */
export function calculateWordCount(content: string): number {
  if (!content) return 0;
  // Remove HTML tags and count words
  const text = content.replace(/<[^>]*>/g, ' ');
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
}

/**
 * Extracts and maps prose summaries from the novel document (used by list views).
 * Leverages cached summaries instead of performing direct collection fetches.
 */
export function getTextsWithScenes(
  novel: Novel
): Array<ContentDocument & { id: string }> {
  // Use the strictly-typed summaries from the novel context
  const contents = novel.summaries.contents;

  return Object.values(contents)
    .sort((a, b) => {
      // Latest first
      const timeA = toTimestamp(a.updatedAt);
      const timeB = toTimestamp(b.updatedAt);
      return timeB - timeA;
    })
    .map(summary => {
      // Create a compatible ContentDocument shape for legacy UI consuming this
      const doc: ContentDocument & { id: string } = {
        id: summary.id,
        name: summary.name,
        // Map summary 'preview' to 'scratchpad' field for UI-level card rendering
        scratchpad: summary.preview,
        scratchpadVersion: 0,
        currentDayKey: '',
        sceneIds: summary.sceneIds ?? [],
        chapterId: summary.chapterId ?? null,
        wordCount: summary.wordCount,
        audit: {
          createdAt: summary.updatedAt,
          updatedAt: summary.updatedAt,
        }
      };
      return doc;
    });
}
